import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import FunctionForm from "./FunctionForm";
import { useFunctions } from "../../hooks/useFunctions"; // Hook personalizado
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import { useAuthContext } from "../../context/AuthProvider";

const FunctionsDashboard = () => {
  const [open, setOpen] = useState(false);
  const [editFunction, setEditFunction] = useState(null); // Estado para edición
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  // Hook para obtener las ventanas y las mutaciones
  const { user } = useAuthContext();
  const { functions, isLoading, error, createMutation, updateMutation, deleteMutation } = useFunctions();

  // Mostrar mensaje de alerta
  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  // Función para manejar la creación o edición
  const handleSubmitFunction = (data) => {
    if (editFunction) {
      updateMutation.mutate(
        { id: editFunction.funid, data: { ...data, updbyid: user.EmpID } },
        {
          onSuccess: () => {
            showAlert("Funcion actualizada con éxito");
            setOpen(false);
            setEditFunction(null);
          },
          onError: (error) => {
            showAlert(error.message || "Error al actualizar la funcion", "error");
          },
        }
      );
    } else {
      data.crebyid = user.EmpID; //temporal hasta validad la sesion
      createMutation.mutate(data, {
        onSuccess: () => {
          showAlert("Funcion creada con éxito");
          setOpen(false);
        },
        onError: (error) => {
          showAlert(error.message || "Error al crear la funcion", "error");
        },
      });
    }
  };

  // Función para desactivar una ventana
  const handleDeactivateFunction = (id) => {
    deleteMutation.mutate(id, {
      onSuccess: () => showAlert("Funcion desactivada con éxito"),
      onError: (error) => showAlert(error.response.data.error || "Error al desactivar la funcion", "error"),
    });
  };

  if (isLoading) return <Typography>Cargando funciones...</Typography>;
  if (error) return <Typography>Error al cargar funciones: {error.message}</Typography>;

  return (
    <Paper sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Funciones</Typography>
        <Tooltip title="Agregar Funcion">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditFunction(null);
              setOpen(true);
            }}
          >
            Nueva Funcion
          </Button>
        </Tooltip>
      </Box>

      {/* Lista de funciones */}
      <List>
        {functions.map((functionx) => (
          <ListItem
            key={functionx.funid}
            sx={{ bgcolor: "white", mb: 1, borderRadius: 1, boxShadow: 1 }}
          >
            <ListItemText
              primary={functionx.funnom}
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    Descripcion: {functionx.fundes}
                  </Typography>
                </>
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Estado de la ventana */}
              <Chip
                label={functionx.estreg}
                color={functionx.estreg === "A" ? "success" : "error"}
                size="small"
              />
              {/* Botón para editar */}
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditFunction(functionx);
                    setOpen(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {/* Botón para desactivar */}
              {functionx.estreg === "A" && (
                <Tooltip title="Desactivar">
                  <IconButton
                    size="small"
                    onClick={() => handleDeactivateFunction(functionx.funid)}
                  >
                    <BlockIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      {/* Formulario para crear/editar ventanas */}
      {open && (
        <FunctionForm
          open={open}
          handleClose={() => {
            setOpen(false);
            setEditFunction(null);
          }}
          onSubmit={handleSubmitFunction}
          defaultValues={editFunction || {}}
        />
      )}

      {/* Notificaciones */}
      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default FunctionsDashboard;
